/**
 * Coaching Sessions Page
 *
 * Manage 1:1 coaching sessions.
 */

import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRole } from '@/contexts/RoleContext';
import { canWriteCoaching } from '@aica/core/rbac';
import { useApiClient } from '@/hooks/useApiClient';
import { logError } from '@/lib/logger';
import { distinctByAgentId } from '@/lib/employee-row-key';
import { formatDateTime } from '@/lib/utils';
import { PageBody } from '@/components/layout/PageBody';
import { PageHeader } from '@/components/layout/PageHeader';
import { Breadcrumb, BreadcrumbItem } from '@/components/navigation/breadcrumb';
import { Button } from '@/components/ui/button';
import { TableSkeleton } from '@/components/ui/skeletons';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CoachingSessionModal } from '@/components/supervisor';
import { CoachingEmptyHint } from '@/components/coaching/CoachingEmptyHint';
import {
  Calendar,
  Plus,
  User,
  CheckCircle,
  XCircle,
  Clock,
  GraduationCap,
} from 'lucide-react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { CoachingSession, CoachingSessionsResponse } from '@/lib/api';

type SessionStatusFilter = 'scheduled' | 'completed' | 'cancelled' | 'all';

const coachingSessionsQueryKey = (
  orgId: string | null,
  statusFilter: SessionStatusFilter
) => ['coaching', 'sessions', orgId, statusFilter] as const;

const coachingAgentsQueryKey = (orgId: string | null) =>
  ['coaching', 'agents', orgId] as const;

export const Route = createFileRoute('/coaching/sessions')({
  component: CoachingSessionsPage,
});

function CoachingSessionsPage() {
  const { t } = useTranslation(['dashboard', 'common']);
  useDocumentTitle(t('coaching.sessions.title'));
  const { selectedOrgId, globalRole, isOwner } = useRole();
  // Scheduling is role ∧ grant (`agent.create`): a grant opens this page for
  // reading only (#1645).
  const canWrite = canWriteCoaching(globalRole, isOwner);
  const { apiClient } = useApiClient();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<SessionStatusFilter>('scheduled');
  const [modalOpen, setModalOpen] = useState(false);

  const sessionsQuery = useQuery({
    queryKey: coachingSessionsQueryKey(selectedOrgId, statusFilter),
    enabled: !!selectedOrgId,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[2] === selectedOrgId ? previousData : undefined,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    queryFn: () => {
      const params: {
        limit: number;
        status?: 'scheduled' | 'completed' | 'cancelled';
      } = { limit: 50 };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      return apiClient.agents.getCoachingSessions(params);
    },
  });

  const agentsQuery = useQuery({
    queryKey: coachingAgentsQueryKey(selectedOrgId),
    enabled: !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    // The agent picker lists every agent, not the top 100 by score (#1140).
    queryFn: () => apiClient.agents.getTeamLeaderboard({ scope: 'team' }),
  });

  useEffect(() => {
    if (!sessionsQuery.error) return;
    logError(
      t('coaching.sessions.failedToFetch'),
      {},
      sessionsQuery.error instanceof Error ? sessionsQuery.error : undefined
    );
  }, [sessionsQuery.error, sessionsQuery.errorUpdatedAt, t]);

  useEffect(() => {
    if (!agentsQuery.error) return;
    logError(
      t('coaching.sessions.failedToFetchAgents'),
      {},
      agentsQuery.error instanceof Error ? agentsQuery.error : undefined
    );
  }, [agentsQuery.error, agentsQuery.errorUpdatedAt, t]);

  const sessions = (sessionsQuery.data?.sessions ?? []) as CoachingSession[];
  const agents = distinctByAgentId(agentsQuery.data?.entries ?? []).map((entry) => ({
    id: entry.agentId,
    name: entry.agentName,
  }));
  const loading =
    sessionsQuery.isLoading || (agentsQuery.isLoading && agents.length === 0);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'cancelled':
        return <XCircle className="w-4 h-4 text-fg-muted" />;
      default:
        return <Clock className="w-4 h-4 text-primary" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <Badge variant="success" outline>
            {t('coaching.sessions.status.completed')}
          </Badge>
        );
      case 'cancelled':
        return (
          <Badge variant="default" outline>
            {t('coaching.sessions.status.cancelled')}
          </Badge>
        );
      default:
        return (
          <Badge variant="primary" outline>
            {t('coaching.sessions.status.scheduled')}
          </Badge>
        );
    }
  };

  const pageHeader = (actions?: ReactNode) => (
    <PageHeader
      sticky
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
          <BreadcrumbItem current>{t('coaching.sessions.title')}</BreadcrumbItem>
        </Breadcrumb>
      }
      icon={GraduationCap}
      title={t('coaching.sessions.title')}
      subtitle={t('coaching.sessions.description')}
      actions={actions}
    />
  );

  if (loading && sessions.length === 0) {
    return (
      <PageBody>
        {pageHeader()}
        <TableSkeleton />
      </PageBody>
    );
  }

  return (
    <PageBody>
      {pageHeader(
        canWrite ? (
          <Button onClick={() => setModalOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            {t('coaching.scheduleSession')}
          </Button>
        ) : undefined
      )}
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex items-center gap-4 bg-bg-surface p-4 rounded-card border border-border">
          <div className="w-48">
            <Select
              value={statusFilter}
              onValueChange={(val) =>
                setStatusFilter(val as 'scheduled' | 'completed' | 'cancelled' | 'all')
              }
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t('coaching.sessions.statusFilterPlaceholder')}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="scheduled">
                  {t('coaching.sessions.status.scheduled')}
                </SelectItem>
                <SelectItem value="completed">
                  {t('coaching.sessions.status.completed')}
                </SelectItem>
                <SelectItem value="cancelled">
                  {t('coaching.sessions.status.cancelled')}
                </SelectItem>
                <SelectItem value="all">{t('coaching.sessions.allStatuses')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="rounded-md border border-border bg-bg-surface overflow-hidden">
          <div className="w-full text-sm text-left">
            <table className="w-full">
              <thead className="bg-bg-elevated text-xs uppercase text-fg-muted font-medium border-b border-border">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left">
                    {t('coaching.sessions.table.dateTime')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    {t('coaching.sessions.table.agent')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    {t('coaching.sessions.table.status')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    {t('coaching.sessions.table.topicNotes')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-elevated">
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-fg-muted">
                      <CoachingEmptyHint
                        kind="sessions"
                        title={t('common:common.noSessions')}
                        failed={Boolean(sessionsQuery.error)}
                        statusFilter={statusFilter}
                      />
                    </td>
                  </tr>
                ) : (
                  sessions.map((session) => (
                    <tr
                      key={session.id}
                      className="hover:bg-bg-elevated/50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-fg">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-fg-muted" />
                          {formatDateTime(session.scheduledAt)}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-fg">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-fg-muted" />
                          {session.agentName || t('coaching.sessions.unknownAgent')}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(session.status)}
                          {getStatusBadge(session.status)}
                        </div>
                      </td>
                      <td className="px-4 py-3 max-w-md truncate text-fg-muted">
                        {session.notes || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <CoachingSessionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={(createdSession) => {
          if (!selectedOrgId) return;
          if (statusFilter !== 'scheduled' && statusFilter !== 'all') return;

          queryClient.setQueryData<CoachingSessionsResponse>(
            coachingSessionsQueryKey(selectedOrgId, statusFilter),
            (previous) => {
              if (!previous) {
                return { sessions: [createdSession], total: 1 };
              }

              const exists = previous.sessions.some(
                (session) => session.id === createdSession.id
              );
              if (exists) return previous;

              return {
                ...previous,
                sessions: [createdSession, ...previous.sessions].slice(0, 50),
                total: previous.total + 1,
              };
            }
          );
        }}
        agents={agents}
      />
    </PageBody>
  );
}
